/* =========================================================
   kimonodatta. About — Shopify 版（assets/kd-about.js）
   kd-about-* セクションの外枠（.kd-root[data-kd-section]）ごとに初期化する。
   テーマエディタでセクションが再描画されたとき（shopify:section:load）は
   そのセクションだけを作り直し、選択したブロックが見えるように表示を合わせる。
   ========================================================= */
(() => {
  'use strict';

  // 複数のセクションがこのファイルを読み込んでも 1 回だけ実行する
  if (window.kdAbout) return;

  // 画像のフェードイン用（JS が動かない環境では画像を普通に表示する）
  document.documentElement.classList.add('kd-js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const spQuery = window.matchMedia('(max-width: 767px)');
  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  const easeOut = (v) => 1 - Math.pow(1 - v, 3);

  /* ---------- ① FV：写真のエディトリアル配置＋スクロール ---------- */
  function initFv(root, signal) {
    const fv = root.querySelector('.kd-fv');
    const stage = root.querySelector('.kd-fv__stage');
    const scene = root.querySelector('.kd-fv__scene');
    if (!fv || !stage || !scene) return null;

    const all = Array.from(stage.querySelectorAll('.kd-fv__photo'));
    // 枚数ごとの配置プリセット（1 枚、または 3〜8 枚。2 枚は 3 枚のプリセットを使う）
    const n = all.length;
    if (n) stage.dataset.count = String(n === 1 ? 1 : Math.min(8, Math.max(3, n)));
    const photos = all.slice(0, n === 1 ? 1 : 8);
    const single = photos.length === 1;
    const items = Array.from(root.querySelectorAll('.kd-statement__item'));

    // PC：写真ごとの速度係数（デザインソース fvPhotos の SPD）
    const SPD = [1.0, 1.35, 0.8, 1.2, 0.9, 1.3, 1.1, 0.75];

    let metrics = [];
    let scale = 1;
    let midY = 310;
    let sp = spQuery.matches;
    let pinTop = 0;
    let distance = 1;
    let ticking = false;

    // 設計座標（CSS 変数 --y / --h）とエリア幅から、写真ごとの係数を求める
    function measure() {
      sp = spQuery.matches;
      pinTop = parseFloat(getComputedStyle(scene).top) || 0;
      distance = Math.max(1, fv.offsetHeight - scene.offsetHeight);
      const stageCs = getComputedStyle(stage);
      const designW = parseFloat(stageCs.getPropertyValue('--fv-w')) || (sp ? 390 : 1440);
      midY = (parseFloat(stageCs.getPropertyValue('--fv-h')) || 620) / 2;
      scale = stage.clientWidth / designW;
      metrics = photos.map((el, k) => {
        const cs = getComputedStyle(el);
        const y = parseFloat(cs.getPropertyValue('--y')) || 0;
        const h = parseFloat(cs.getPropertyValue('--h')) || 0;
        return { el, cy: y + h / 2, spd: single ? 0.6 : SPD[k % 8] };
      });
    }

    function renderPhotos(p) {
      for (const m of metrics) {
        let dy, op;
        if (sp) {
          // 下にある写真ほど大きく動く → 流れても重ならない
          dy = (140 + m.cy * 0.9) * p;
          op = 1 - p * (0.95 + m.cy / 1200);
        } else {
          dy = ((m.cy - midY) * 0.7 + 280) * p * m.spd;
          op = 1 - p * (0.9 + 0.35 * m.spd);
        }
        // 写真は左右に動かさず、下方向へ流れながらフェードアウト
        m.el.style.transform = `translate3d(0, ${(dy * scale).toFixed(1)}px, 0)`;
        m.el.style.opacity = clamp01(op).toFixed(3);
      }
    }

    function render() {
      ticking = false;
      if (reducedMotion.matches) return;
      // シーン全体の進捗 t（0〜1）：シーンが画面に固定されてからのスクロール量 ÷ 固定区間の長さ
      const t = clamp01((pinTop - fv.getBoundingClientRect().top) / distance);

      // 前半：写真が下へ流れながらフェードアウト
      renderPhotos(clamp01(t / 0.5));

      // 後半：statement → 縦線 → 2 段落目 → ロゴ の順にフェードイン
      const q = clamp01((t - 0.45) / 0.35);
      items.forEach((el, k) => {
        const v = easeOut(clamp01((q - k * 0.18) / 0.46));
        el.style.opacity = v.toFixed(3);
        el.style.transform = v < 1 ? `translate3d(0, ${((1 - v) * 16).toFixed(1)}px, 0)` : '';
      });
    }

    function request() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(render);
      }
    }

    function reset() {
      for (const el of [...photos, ...items]) {
        el.style.transform = '';
        el.style.opacity = '';
      }
    }

    const remeasure = () => { measure(); request(); };
    measure();
    render();
    // テーマによってはページのスクロールが window ではなく .page-wrapper などの要素で起きるため、
    // どの要素のスクロールも拾えるよう document でキャプチャする
    document.addEventListener('scroll', request, { passive: true, capture: true, signal });
    window.addEventListener('resize', remeasure, { signal });
    window.addEventListener('load', remeasure, { signal });
    spQuery.addEventListener('change', remeasure, { signal });
    reducedMotion.addEventListener('change', () => (reducedMotion.matches ? reset() : request()), { signal });

    // テーマエディタ：ステートメントのブロックを選んだら写真を隠して文章を表示し、
    // 写真ブロックを選んだら写真の表示に戻す（セクション設定「編集画面でのプレビュー」と同じ切り替え）
    const editorDefault = root.dataset.editorView || '';
    return {
      selectBlock(blockEl) {
        if (!('editorView' in root.dataset)) return;
        root.dataset.editorView = blockEl.classList.contains('kd-statement__item') ? 'statement' : 'photos';
      },
      deselectBlock() {
        if ('editorView' in root.dataset) root.dataset.editorView = editorDefault;
      },
    };
  }

  /* ---------- ④ 格子 SVG：5 秒ループ。画面に入っている間だけ再生 ---------- */
  function initLattice(root, signal) {
    const lattices = root.querySelectorAll('.kd-lattice');
    if (!lattices.length) return;
    if (!('IntersectionObserver' in window)) {
      lattices.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle('is-inview', entry.isIntersecting);
      }
    }, { threshold: 0.1 });
    lattices.forEach((el) => io.observe(el));
    signal.addEventListener('abort', () => io.disconnect());
  }

  /* ---------- ④ カルーセル：Expanding dots ＋ スワイプ ＋ キーボード ---------- */
  function initCarousel(root, signal) {
    const viewport = root.querySelector('.kd-carousel__viewport');
    const track = root.querySelector('.kd-carousel__track');
    const slides = Array.from(root.querySelectorAll('.kd-slide'));
    const dots = Array.from(root.querySelectorAll('.kd-dots__dot'));
    if (!viewport || !track || !slides.length) return null;

    let index = 0;

    // スライド位置の差分から移動量を求める（PC 1040px / SP 320px 相当、幅に追従）
    const offsetOf = (i) => slides[i].offsetLeft - slides[0].offsetLeft;

    function setTranslate(x) {
      track.style.transform = `translate3d(${-x}px, 0, 0)`;
    }

    function goTo(i, { focusDot = false } = {}) {
      index = Math.max(0, Math.min(slides.length - 1, i));
      setTranslate(offsetOf(index));
      slides.forEach((s, k) => s.setAttribute('aria-hidden', String(k !== index)));
      dots.forEach((d, k) => {
        const on = k === index;
        d.setAttribute('aria-selected', String(on));
        d.tabIndex = on ? 0 : -1;
      });
      if (focusDot && dots[index]) dots[index].focus();
    }

    // ドット：クリック／矢印キー（tablist のキーボード操作）
    dots.forEach((dot, k) => {
      dot.addEventListener('click', () => goTo(k), { signal });
      dot.addEventListener('keydown', (e) => {
        const map = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: slides.length - 1 };
        if (e.key in map) {
          e.preventDefault();
          goTo(map[e.key], { focusDot: true });
        }
      }, { signal });
    });

    // スライド領域にフォーカスしているときも ←→ で操作
    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(index + (e.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });

    // スワイプ（タッチ／マウスドラッグ）
    let startX = 0;
    let startY = 0;
    let startT = 0;
    let dragging = false;
    let locked = false; // 横方向のドラッグと確定したか
    let pointerId = null;

    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      locked = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startT = performance.now();
    }, { signal });

    viewport.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!locked) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { dragging = false; return; } // 縦スクロールを優先
        locked = true;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture(pointerId);
      }
      // 端では抵抗をつける
      const atEdge = (index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0);
      setTranslate(offsetOf(index) - (atEdge ? dx * 0.3 : dx));
    }, { signal });

    function endDrag(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      dragging = false;
      if (!locked) return;
      viewport.classList.remove('is-dragging');
      const dx = e.clientX - startX;
      const velocity = Math.abs(dx) / Math.max(1, performance.now() - startT);
      const threshold = Math.min(80, slides[0].offsetWidth * 0.2);
      if (Math.abs(dx) > threshold || velocity > 0.5) {
        goTo(index + (dx < 0 ? 1 : -1));
      } else {
        goTo(index);
      }
    }
    viewport.addEventListener('pointerup', endDrag, { signal });
    viewport.addEventListener('pointercancel', endDrag, { signal });
    viewport.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    window.addEventListener('resize', () => goTo(index), { signal });
    goTo(0);

    // テーマエディタ：選んだスライドを表示
    return {
      selectBlock(blockEl) {
        const i = slides.indexOf(blockEl.closest('.kd-slide'));
        if (i >= 0) goTo(i);
      },
    };
  }

  /* ---------- 画像の読み込み：先読み＋フェードイン ----------
     ブラウザ標準の遅延読み込みは、テーマのスクロール要素（PC は .page-wrapper）の中では
     画面に入る直前まで読み込みが始まらず、速くスクロールするとグレーの枠が見えてしまう。
     そこで、①実際のスクロール要素を基準に画面 2 つ分先から読み込みを始め、
     ②ファーストビューの画像が出たら、残りの画像を上から順に先読みしておく。 */
  function scrollRoot() {
    const pw = document.querySelector('.page-wrapper');
    if (!pw) return null;
    const oy = getComputedStyle(pw).overflowY;
    return /(auto|scroll)/.test(oy) && pw.scrollHeight > pw.clientHeight ? pw : null;
  }

  function waitFor(img) {
    return new Promise((resolve) => {
      if (img.complete) return resolve();
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
      setTimeout(resolve, 8000);
    });
  }

  function initImages(scope) {
    const imgs = Array.from(scope.querySelectorAll('.kd-ph--img > img'));
    if (!imgs.length) return;

    imgs.forEach((img) => {
      const done = () => img.classList.add('is-loaded');
      if (img.complete && img.naturalWidth) done();
      else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });

    const lazy = imgs.filter((img) => img.loading === 'lazy');
    if (!lazy.length) return;
    const load = (img) => { img.loading = 'eager'; };

    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          load(e.target);
          io.unobserve(e.target);
        });
      }, { root: scrollRoot(), rootMargin: '200% 100%' });
      lazy.forEach((img) => io.observe(img));
    }

    // 上から順に、3 枚ずつ並行して先読みする
    const queue = lazy.slice();
    const worker = async () => {
      while (queue.length) {
        const img = queue.shift();
        if (img.complete && img.naturalWidth) continue;
        io?.unobserve(img);
        load(img);
        await waitFor(img);
      }
    };
    const preload = () => { for (let i = 0; i < 3; i++) worker(); };

    // ファーストビューの画像（eager）を優先し、それが終わったら（最長 1.5 秒で）先読みを始める
    const first = imgs.filter((img) => img.loading !== 'lazy');
    Promise.race([
      Promise.all(first.map(waitFor)),
      new Promise((r) => setTimeout(r, 1500)),
    ]).then(preload);
  }

  /* ---------- セクション単位の初期化／破棄 ---------- */
  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const parts = [];
    if (root.querySelector('.kd-fv')) parts.push(initFv(root, signal));
    if (root.querySelector('.kd-lattice')) initLattice(root, signal);
    if (root.querySelector('.kd-carousel')) parts.push(initCarousel(root, signal));
    instances.set(root, { controller, parts: parts.filter(Boolean) });
  }

  function unmount(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.controller.abort();
    instances.delete(root);
  }

  const rootsIn = (el) => (el.matches?.('.kd-root[data-kd-section]') ? [el] : Array.from(el.querySelectorAll('.kd-root[data-kd-section]')));

  function init() {
    rootsIn(document).forEach(mount);
    initImages(document);
  }

  // テーマエディタのイベント
  document.addEventListener('shopify:section:load', (e) => {
    rootsIn(e.target).forEach(mount);
    initImages(e.target);
  });
  document.addEventListener('shopify:section:unload', (e) => rootsIn(e.target).forEach(unmount));
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('.kd-root[data-kd-section]');
    const inst = root && instances.get(root);
    if (!inst) return;
    inst.parts.forEach((part) => part.selectBlock?.(e.target));
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.closest('.kd-root[data-kd-section]');
    const inst = root && instances.get(root);
    if (!inst) return;
    inst.parts.forEach((part) => part.deselectBlock?.(e.target));
  });

  window.kdAbout = { mount, unmount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
